import React, { useState, useEffect } from 'react';
import { Copy, Check, Key, FileText, Zap, Shield, Code, ChevronRight } from 'lucide-react';

const ApiKeyPage = () => {
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    setLoading(true);
    try {
      // Replace with your actual endpoint
      const response = await fetch('/api/eval-key/', {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.key);
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateNewKey = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/eval-key/generate/', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.key);
      }
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const codeExamples = {
    python: `import requests

url = "https://yourapi.com/api/evaluate-cv/"
headers = {
    "Authorization": "Bearer ${apiKey || 'YOUR_API_KEY'}",
    "Content-Type": "application/json"
}

# Method 1: Send CV text directly
data = {
    "cv_text": "Your CV content here...",
    "eval_type": "GENERAL"
}

response = requests.post(url, json=data, headers=headers)
result = response.json()

print(f"Score: {result['score']}")
print(f"Analysis: {result['analysis']}")
print(f"Feedback: {result['feedback']}")`,

    javascript: `const apiKey = '${apiKey || 'YOUR_API_KEY'}';

// Method 1: Send CV text
const evaluateCV = async (cvText) => {
  const response = await fetch('https://yourapi.com/api/evaluate-cv/', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${apiKey}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      cv_text: cvText,
      eval_type: 'GENERAL'
    })
  });
  
  const result = await response.json();
  console.log('Score:', result.score);
  console.log('Analysis:', result.analysis);
  console.log('Feedback:', result.feedback);
  return result;
};

// Method 2: Upload CV file
const uploadCV = async (file) => {
  const formData = new FormData();
  formData.append('cv_file', file);
  formData.append('eval_type', 'GENERAL');
  
  const response = await fetch('https://yourapi.com/api/evaluate-cv/', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: formData
  });
  
  return await response.json();
};`,

    curl: `# Evaluate CV with text
curl -X POST https://yourapi.com/api/evaluate-cv/ \\
  -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cv_text": "Your CV content here...",
    "eval_type": "GENERAL"
  }'

# Upload CV file
curl -X POST https://yourapi.com/api/evaluate-cv/ \\
  -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -F "cv_file=@/path/to/cv.pdf" \\
  -F "eval_type=GENERAL"`
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                  <Key className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">CV Evaluation API</h1>
              </div>
              <p className="text-gray-600 text-lg mb-6">
                Powerful AI-driven CV analysis and scoring system. Upload CVs and receive instant feedback, 
                detailed analysis, and actionable insights.
              </p>
              
              {/* Key Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Instant Analysis</h3>
                    <p className="text-sm text-gray-600">Get results in seconds</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-green-50 p-2 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Detailed Feedback</h3>
                    <p className="text-sm text-gray-600">AI-powered insights</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-purple-50 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Secure & Private</h3>
                    <p className="text-sm text-gray-600">Your data protected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* API Key Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Your API Key</h2>
              <button
                onClick={generateNewKey}
                disabled={loading}
                className="text-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate New Key'}
              </button>
            </div>
            
            {apiKey ? (
              <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-gray-200">
                <code className="flex-1 font-mono text-sm text-gray-800 break-all">
                  {apiKey}
                </code>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-8 border border-gray-200 text-center">
                <p className="text-gray-500">Click "Generate New Key" to get started</p>
              </div>
            )}
            
            <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Keep your API key secure. Never share it publicly or commit it to version control.</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              {['overview', 'quickstart', 'examples', 'response'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-4 font-medium text-sm capitalize transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="p-8">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">API Overview</h2>
                  <p className="text-gray-600 mb-4">
                    The CV Evaluation API allows you to programmatically analyze CVs/resumes using advanced AI. 
                    Submit a CV and receive a comprehensive evaluation including:
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">📊 Scoring</h3>
                      <p className="text-sm text-gray-600">
                        Get a numerical score (0-100) based on industry standards, experience relevance, 
                        and presentation quality.
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">📝 Analysis</h3>
                      <p className="text-sm text-gray-600">
                        Detailed breakdown of strengths, weaknesses, and areas for improvement across 
                        multiple dimensions.
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">💡 AI Feedback</h3>
                      <p className="text-sm text-gray-600">
                        Actionable recommendations powered by AI to help improve the CV's effectiveness 
                        and impact.
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">🎯 Evaluation Types</h3>
                      <p className="text-sm text-gray-600">
                        Multiple evaluation modes: GENERAL, TECHNICAL, EXECUTIVE, CREATIVE, and more 
                        tailored assessments.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Base URL</h3>
                  <code className="text-sm text-blue-800">https://yourapi.com/api/</code>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Authentication</h3>
                  <p className="text-gray-600 mb-2">
                    All requests must include your API key in the Authorization header:
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <code className="text-sm text-green-400">
                      Authorization: Bearer YOUR_API_KEY
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* Quickstart Tab */}
            {activeTab === 'quickstart' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Start Guide</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                          1
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Get Your API Key</h3>
                      </div>
                      <p className="text-gray-600 ml-11">
                        Copy your API key from the section above. You'll need this for all API requests.
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                          2
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Make Your First Request</h3>
                      </div>
                      <div className="ml-11">
                        <p className="text-gray-600 mb-3">
                          Use the endpoint <code className="bg-gray-100 px-2 py-1 rounded text-sm">/evaluate-cv/</code> to analyze a CV:
                        </p>
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-sm text-gray-100">
{`POST /api/evaluate-cv/
Content-Type: application/json
Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}

{
  "cv_text": "John Doe\\nSoftware Engineer\\n...",
  "eval_type": "GENERAL"
}`}
                          </pre>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                          3
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Process the Response</h3>
                      </div>
                      <p className="text-gray-600 ml-11 mb-3">
                        The API returns a JSON response with the evaluation results. See the Response tab for details.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-2">💡 Pro Tips</h4>
                  <ul className="space-y-2 text-sm text-yellow-800">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Use meaningful evaluation types for better targeted feedback</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Include complete CV text for most accurate analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Rate limit: 100 requests per hour per API key</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Examples Tab */}
            {activeTab === 'examples' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Code Examples</h2>
                  
                  <div className="space-y-4">
                    {/* Python Example */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Code className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Python</h3>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100">{codeExamples.python}</pre>
                      </div>
                    </div>

                    {/* JavaScript Example */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Code className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">JavaScript / Node.js</h3>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100">{codeExamples.javascript}</pre>
                      </div>
                    </div>

                    {/* cURL Example */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Code className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">cURL</h3>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100">{codeExamples.curl}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Response Tab */}
            {activeTab === 'response' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">API Response Format</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Success Response (200 OK)</h3>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100">
{`{
  "success": true,
  "data": {
    "score": 85,
    "analysis": {
      "strengths": [
        "Clear professional summary",
        "Relevant technical skills well-presented",
        "Quantifiable achievements included"
      ],
      "weaknesses": [
        "Gaps in employment history not explained",
        "Limited soft skills mentioned"
      ],
      "overall_impression": "Strong technical profile with room for improvement..."
    },
    "feedback": [
      {
        "category": "Structure",
        "suggestion": "Consider adding a projects section to showcase work",
        "priority": "medium"
      },
      {
        "category": "Content",
        "suggestion": "Add more specific metrics to quantify achievements",
        "priority": "high"
      }
    ],
    "eval_type": "GENERAL",
    "evaluated_at": "2024-12-15T22:30:45Z"
  }
}`}
                        </pre>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Response Fields</h3>
                      <div className="space-y-3">
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-sm font-mono">
                              score
                            </code>
                            <div className="flex-1">
                              <p className="text-sm text-gray-600">
                                Integer (0-100) - Overall CV quality score
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-sm font-mono">
                              analysis
                            </code>
                            <div className="flex-1">
                              <p className="text-sm text-gray-600">
                                Object - Detailed breakdown including strengths, weaknesses, and overall impression
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-sm font-mono">
                              feedback
                            </code>
                            <div className="flex-1">
                              <p className="text-sm text-gray-600">
                                Array - Actionable suggestions with priority levels (high, medium, low)
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Error Response (4xx/5xx)</h3>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100">
{`{
  "success": false,
  "error": {
    "code": "INVALID_API_KEY",
    "message": "The provided API key is invalid or expired",
    "details": "Please check your API key and try again"
  }
}`}
                        </pre>
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Common Error Codes</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <code className="text-red-600">INVALID_API_KEY</code>
                          <span className="text-gray-600">API key is invalid or missing</span>
                        </div>
                        <div className="flex justify-between">
                          <code className="text-red-600">RATE_LIMIT_EXCEEDED</code>
                          <span className="text-gray-600">Too many requests in time window</span>
                        </div>
                        <div className="flex justify-between">
                          <code className="text-red-600">INVALID_INPUT</code>
                          <span className="text-gray-600">CV text is empty or malformed</span>
                        </div>
                        <div className="flex justify-between">
                          <code className="text-red-600">PROCESSING_ERROR</code>
                          <span className="text-gray-600">Error during CV analysis</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Need help? Contact support or check our documentation for more details.</p>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyPage;